import { Send } from 'lucide-react';
import { fetchWithAuth } from './authService';
import { API_ENDPOINTS } from '../utils/constants';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

const CommentsModal = ({
  open,
  onClose,
  questionId,
  showToast,
  onCommentAdded,
}) => {
  const [comments, setComments] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [posting, setPosting] = useState(false);
  const { ref, inView } = useInView({
    threshold: 0,
    rootMargin: '100px',
  });
  const [newComment, setNewComment] = useState('');
  const lastFetchedPage = useRef(0);

  // Fetch comments
  const fetchComments = useCallback(
    async pageNum => {
      // Prevent duplicate requests for the same page
      if (loading || pageNum <= lastFetchedPage.current) return;

      setLoading(true);
      try {
        const res = await fetchWithAuth(
          `${API_ENDPOINTS.CHANCE_ALL_COMMENTS(questionId)}?page=${pageNum}&limit=10`
        );
        const data = await res.json();

        if (!data.comments || data.comments.length === 0) {
          setHasMore(false);
          return;
        }

        setComments(prev =>
          pageNum === 1 ? data.comments : [...prev, ...data.comments]
        );
        setHasMore(data.hasNextPage);
        lastFetchedPage.current = pageNum; // Track the last fetched page
      } catch (err) {
        console.error('Error fetching comments:', err);
      } finally {
        setLoading(false);
      }
    },
    [questionId, loading]
  );

  // Initial load
  useEffect(() => {
    if (open && questionId) {
      setComments([]);
      setPage(1);
      setHasMore(true);
      lastFetchedPage.current = 0; // Reset last fetched page
      fetchComments(1);
    }
  }, [open, questionId]);

  // Load more on scroll
  useEffect(() => {
    if (inView && hasMore && !loading && page > lastFetchedPage.current) {
      fetchComments(page);
    }
  }, [inView, hasMore, loading, page, fetchComments]);

  const handlePostComment = async e => {
    if (e) e.preventDefault();
    if (!newComment.trim()) return;

    setPosting(true);
    try {
      const res = await fetchWithAuth(API_ENDPOINTS.CHANCE_COMMENTS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          questionId,
          comment: newComment,
        }),
      });

      if (!res.ok) throw new Error('Failed to post comment');

      setNewComment('');
      // ✅ Update parent question card count
      if (onCommentAdded) onCommentAdded();

      // Refresh comments by resetting to page 1
      setPage(1);
      setHasMore(true);
      lastFetchedPage.current = 0;
      fetchComments(1);
    } catch (err) {
      console.error('Error posting comment:', err);
      showToast('Failed to post comment');
    } finally {
      setPosting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-end',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff',
          borderTopLeftRadius: '16px',
          borderTopRightRadius: '16px',
          width: '100%',
          maxHeight: '70vh',
          minHeight: '200px',
          overflowY: 'auto',
          padding: '1rem',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div
          style={{
            width: '40px',
            height: '4px',
            background: '#ccc',
            borderRadius: '2px',
            margin: '0 auto 0.5rem',
          }}
        />
        <h4 style={{ color: '#DB0F11', marginBottom: '1rem' }}>Comments</h4>

        {comments.length === 0 && !loading ? (
          <p style={{ textAlign: 'center', color: '#666', margin: '2rem 0' }}>
            No comments yet
          </p>
        ) : (
          comments.map(c => (
            <div
              key={c.id}
              style={{
                display: 'flex',
                gap: '0.75rem',
                padding: '0.75rem 0',
                borderBottom: '1px solid #eee',
              }}
            >
              <img
                src={
                  c.userAvatar ||
                  'https://images.tringbox.com/default_avatar.png'
                }
                alt="avatar"
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  objectFit: 'cover',
                  alignSelf: 'center',
                }}
              />
              <div style={{ flex: 1 }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <strong>{c.userDisplayName || 'Anonymous'}</strong>
                  <small style={{ color: '#666', fontSize: '0.85rem' }}>
                    {new Date(c.createdAt).toLocaleDateString('en-GB', {
                      day: '2-digit',
                      month: 'short',
                    })}
                  </small>
                </div>
                <p style={{ margin: 0, color: '#333', textAlign: 'left' }}>
                  {c.comment}
                </p>
              </div>
            </div>
          ))
        )}

        {loading && <p style={{ textAlign: 'center' }}>Loading...</p>}

        {/* Sentinel for infinite scroll - only show if there are more comments to load */}
        {hasMore && <div ref={ref} style={{ height: '1px' }} />}

        <form
          onSubmit={handlePostComment}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            borderTop: '1px solid #eee',
            paddingTop: '0.75rem',
            position: 'sticky',
            bottom: 0,
            background: 'white',
          }}
        >
          <input
            value={newComment}
            onChange={e => setNewComment(e.target.value)}
            placeholder="Add a comment..."
            style={{
              flex: 1,
              padding: '0.6rem 1rem',
              borderRadius: '1.5rem',
              outline: 'none',
              background: '#f1f5f9',
              color: 'black',
              border: '1px solid #ccc',
              minHeight: '40px',
            }}
          />
          <button
            type="submit"
            disabled={posting || !newComment.trim()}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: posting || !newComment.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            <Send
              style={{
                color: posting || !newComment.trim() ? '#ccc' : '#DB0F11',
              }}
            />
          </button>
        </form>
      </div>
    </div>
  );
};

export default CommentsModal;
